import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { skipWhile, take, tap } from 'rxjs/operators';
import { Opportunity, SalesOpportunitiesService } from '@vendasta/sales-opportunities';

interface SalesOpportunity {
  get(opportunityId, accountGroupId): Observable<Opportunity>;
}

interface OpportunityState {
  opportunity: Opportunity;
  loading: boolean;
}

@Injectable()
export class SalesOpportunityStoreService {
  private opportunityStore: Map<string, BehaviorSubject<OpportunityState>> = new Map<
    string,
    BehaviorSubject<OpportunityState>
  >();

  constructor(@Inject(SalesOpportunitiesService) private readonly salesOpportunityService: SalesOpportunity) {}

  buildId(accountGroupId: string, opportunityId: string): string {
    return [accountGroupId, opportunityId].join(':');
  }

  public getOpportunityState(opportunityId: string, accountGroupId: string): Observable<OpportunityState> {
    const id = this.buildId(accountGroupId, opportunityId);
    if (!this.opportunityStore.has(id)) {
      this.fetchOpportunity(opportunityId, accountGroupId);
    }
    return this.opportunityStore.get(id).pipe(skipWhile((o) => o === undefined));
  }

  fetchOpportunity(opportunityId: string, accountGroupId: string): void {
    const id = this.buildId(accountGroupId, opportunityId);
    if (!this.opportunityStore.has(id)) {
      this.opportunityStore.set(id, new BehaviorSubject<OpportunityState>(undefined));
    }
    if (!accountGroupId || !opportunityId) {
      this.opportunityStore.get(id).next({ opportunity: null, loading: false });
      return;
    }
    const currentValue = this.opportunityStore.get(id).getValue();
    if (currentValue) {
      this.opportunityStore.get(id).next({ opportunity: currentValue.opportunity, loading: true });
    }
    this.salesOpportunityService
      .get(opportunityId, accountGroupId)
      .pipe(
        tap((o) => this.opportunityStore.get(id).next({ opportunity: o, loading: false })),
        take(1),
      )
      .subscribe();
  }

  public refreshOpportunity(opportunityId: string, accountGroupId: string): void {
    this.fetchOpportunity(opportunityId, accountGroupId);
  }
}
